// Round floating chat button for mobile. Opens Shopify Inbox (<shopify-chat>),
// can be dragged, snaps to the nearest side, and never sits on the bottom bar.
if (!customElements.get('fg-chat-fab')) {
  customElements.define(
    'fg-chat-fab',
    class FgChatFab extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.storageKey = 'fg-chat-fab-position';
        this.gap = 8;
        this.dragged = false;
        this.pointerId = null;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.button.addEventListener('click', this.onClick.bind(this));
        if (this.dataset.draggable === 'true') {
          this.button.addEventListener('pointerdown', this.onPointerDown.bind(this));
        }

        // Keep a saved spot on screen after rotation or a browser-bar resize.
        window.addEventListener('resize', () => {
          const bottom = parseFloat(this.style.getPropertyValue('--fab-bottom'));
          if (!Number.isNaN(bottom)) this.apply(this.dataset.side, bottom);
        });

        // Only show the button once Inbox is actually on the page.
        customElements.whenDefined('shopify-chat').then(() => {
          if (!document.querySelector('shopify-chat')) return;
          this.hidden = false;
          this.restore();
        });
      }

      get chat() {
        return document.querySelector('shopify-chat');
      }

      get size() {
        return this.button.offsetWidth;
      }

      // Lowest the button may sit: just above the bottom bar.
      get minBottom() {
        const root = getComputedStyle(document.documentElement);
        const barHeight = parseFloat(root.getPropertyValue('--mobile-sticky-bar-height')) || 0;
        return barHeight + Number(this.dataset.inset || 0) + this.gap * 2;
      }

      // Extra height the button is lifted by (e.g. above the product page's sticky add-to-cart).
      get lift() {
        const y = parseFloat((getComputedStyle(this).translate || '').split(' ')[1]);
        return Number.isNaN(y) ? 0 : -y;
      }

      clampBottom(bottom) {
        const max = window.innerHeight - this.size - this.gap;
        return Math.min(Math.max(bottom, this.minBottom), max);
      }

      onClick(event) {
        if (this.dragged) {
          event.preventDefault();
          this.dragged = false;
          return;
        }
        if (this.chat && typeof this.chat.show === 'function') this.chat.show();
      }

      onPointerDown(event) {
        // One finger at a time: a second touch mid-drag must not make the button jump.
        if (event.button !== 0 || this.pointerId !== null) return;
        this.pointerId = event.pointerId;
        const rect = this.button.getBoundingClientRect();
        this.start = { x: event.clientX, y: event.clientY, left: rect.left, top: rect.top };
        this.last = { x: event.clientX, t: event.timeStamp };
        this.velocityX = 0;
        this.moving = false;
        this.button.setPointerCapture(event.pointerId);
        this.onMoveBound = this.onPointerMove.bind(this);
        this.onUpBound = this.onPointerUp.bind(this);
        this.button.addEventListener('pointermove', this.onMoveBound);
        this.button.addEventListener('pointerup', this.onUpBound);
        this.button.addEventListener('pointercancel', this.onUpBound);
      }

      onPointerMove(event) {
        if (event.pointerId !== this.pointerId) return;
        const dx = event.clientX - this.start.x;
        const dy = event.clientY - this.start.y;
        if (!this.moving && Math.hypot(dx, dy) < 6) return;
        this.moving = true;
        this.classList.add('is-dragging');

        const size = this.size;
        const left = Math.min(Math.max(this.start.left + dx, this.gap), window.innerWidth - size - this.gap);
        const top = Math.min(
          Math.max(this.start.top + dy, this.gap),
          window.innerHeight - this.minBottom - this.lift - size
        );
        // Transform only while dragging: no layout work per frame.
        this.style.transform = `translate3d(${left - this.start.left}px, ${top - this.start.top}px, 0)`;

        const dt = event.timeStamp - this.last.t;
        if (dt > 0) this.velocityX = (event.clientX - this.last.x) / dt;
        this.last = { x: event.clientX, t: event.timeStamp };
      }

      onPointerUp(event) {
        if (event.pointerId !== this.pointerId) return;
        this.pointerId = null;
        this.button.removeEventListener('pointermove', this.onMoveBound);
        this.button.removeEventListener('pointerup', this.onUpBound);
        this.button.removeEventListener('pointercancel', this.onUpBound);
        if (this.button.hasPointerCapture(event.pointerId)) this.button.releasePointerCapture(event.pointerId);
        if (!this.moving) return;

        const rect = this.button.getBoundingClientRect();
        let side = rect.left + rect.width / 2 < window.innerWidth / 2 ? 'left' : 'right';
        // A quick sideways flick wins over where the finger stopped.
        if (Math.abs(this.velocityX) > 0.5) side = this.velocityX < 0 ? 'left' : 'right';
        const bottom = window.innerHeight - rect.bottom - this.lift;
        this.classList.remove('is-dragging');
        // Swallow the click that follows a drag, then re-arm for the next tap.
        this.dragged = true;
        setTimeout(() => (this.dragged = false), 0);
        this.style.transform = '';
        this.apply(side, bottom);

        // Glide from where it was dropped to the edge it snapped to.
        const to = this.button.getBoundingClientRect();
        if (!this.reduceMotion.matches) {
          this.animate(
            [{ transform: `translate3d(${rect.left - to.left}px, ${rect.top - to.top}px, 0)` }, { transform: 'translate3d(0, 0, 0)' }],
            { duration: 380, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' }
          );
        }
        try {
          localStorage.setItem(this.storageKey, JSON.stringify({ side, bottom: this.clampBottom(bottom) }));
        } catch (e) {}
      }

      apply(side, bottom) {
        this.dataset.side = side;
        if (bottom == null) {
          this.style.removeProperty('--fab-bottom');
        } else {
          this.style.setProperty('--fab-bottom', `${this.clampBottom(bottom)}px`);
        }
      }

      restore() {
        let saved = null;
        try {
          saved = JSON.parse(localStorage.getItem(this.storageKey));
        } catch (e) {}
        if (saved && (saved.side === 'left' || saved.side === 'right') && typeof saved.bottom === 'number') {
          this.apply(saved.side, saved.bottom);
        }
      }
    }
  );
}
